import { useCallback, useMemo, useState } from 'react';

import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import ContentPasteGoIcon from '@mui/icons-material/ContentPasteGo';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';

import { useCopyToClipboard, useToastService } from '@@common';

import { ReactCodeComponentModel } from '../../../api/api';
import { serializeWithTypeFirst } from '../../../common/serializeWithTypeFirst';
import { uuid } from '../../../common/uuid';
import ConfirmDialog from '../../../components/ConfirmDialog';
import { VariableScope } from '../../FlowDesigner/Models/VariableScopes/VariableScope';
import useUserInterfaceMetadata from '../Hooks/useUserInterfaceMetadata';
import { Bundle } from '../Models/Bundle';
import { DialogActionModel } from '../Models/DialogActionModel';
import { EmbeddedResourceModel } from '../Models/EmbeddedResourceModel';
import { IComponentModel } from '../Models/IComponentModel';
import { IContainerComponentModel } from '../Models/IContainerComponentModel';
import { SharedComponentModel } from '../Models/SharedComponentModel';
import { ViewModel } from '../Models/ViewModel';
import {
	ComponentPath,
	addComponentAtPath,
	getComponentAtPath,
	getParentInfo,
	resolveSelectedComponent,
	resolveSharedComponentSelection,
	updateComponentAtPath,
} from '../Utils/ComponentTreeUtils';
import { getCollectionItems, getPotentialCollectionItemSelection, resolveVirtualCollectionSelection } from '../Utils/VirtualComponentCollectionUtils';
import {
	ITEM_TYPE_EMBEDDED_RESOURCE,
	ITEM_TYPE_SETTINGS,
	ITEM_TYPE_SHARED_COMPONENT,
	ITEM_TYPE_THEME,
	ITEM_TYPE_VIEW,
	SelectedItem,
	SharedComponentSelectionContext,
} from './AppArtifactPanel';
import { AppSettingsProperties } from './AppSettingsProperties';
import { ComponentProperties } from './ComponentProperties';
import { EmbeddedResourceProperties } from './EmbeddedResourceProperties';
import { SharedComponentProperties } from './SharedComponentProperties';
import { ViewProperties } from './ViewProperties';
import { VirtualCollectionItemProperties } from './VirtualCollectionItemProperties';

type AppItemPropertiesProps = {
	bundle: Bundle | null;
	selectedItem: SelectedItem | null;
	selectedComponentPath: ComponentPath | null;
	selectedSharedComponentContext?: SharedComponentSelectionContext | null;
	reactComponents?: ReactCodeComponentModel[];
	variableScope?: VariableScope;
	onBundleChanged: (bundle: Bundle) => void;
	onComponentChanged?: (updated: IComponentModel) => void;
	onMoveComponentUp?: () => void;
	onMoveComponentDown?: () => void;
	onRemoveComponent?: () => void;
	onSelectedComponentPathChanged?: (path: ComponentPath | null) => void;
};

/**
 * Recursively regenerates IDs for a component and all its descendants
 */
function regenerateComponentIds(component: IComponentModel): IComponentModel {
	const newComponent: IComponentModel = { ...component, Id: uuid() };

	// If it's a container, recursively regenerate IDs for children
	const container = newComponent as IContainerComponentModel;
	if (container.Children && Array.isArray(container.Children)) {
		container.Children = container.Children.map((child) => regenerateComponentIds(child));
	}

	return newComponent;
}

function getComponentFromRoot(root: IContainerComponentModel, path: ComponentPath): IComponentModel | null {
	if (path.length === 0) {
		return null;
	}

	if (path.length === 1 && path[0] === root.Id) {
		return root;
	}

	const relativePath = path[0] === root.Id ? path.slice(1) : path;
	return getComponentAtPath(root.Children ?? [], relativePath);
}

function getTemplateItemFromComponent(component: IComponentModel, templatePropertyNames: string[]): Record<string, unknown> {
	const source = component as unknown as Record<string, unknown>;
	const template: Record<string, unknown> = {};

	for (const propertyName of templatePropertyNames) {
		const value = source[propertyName];
		if (value !== undefined && value !== null && value !== '') {
			template[propertyName] = value;
		}
	}

	return template;
}

const listTemplatePropertyNames = [
	'PrimaryTextField',
	'PrimaryTextFormat',
	'SecondaryTextField',
	'SecondaryTextFormat',
	'SupportingTextField',
	'SupportingTextFormat',
	'IconName',
	'BadgeField',
	'BadgeFormat',
];

const virtualTemplateDefaults: Record<string, Record<string, (component: IComponentModel) => Record<string, unknown>>> = {
	ListWithSideDetail: {
		Items: (component) => getTemplateItemFromComponent(component, listTemplatePropertyNames),
	},
	List: {
		Items: (component) => getTemplateItemFromComponent(component, listTemplatePropertyNames),
	},
	ListMessageChat: {
		Message: (component) =>
			getTemplateItemFromComponent(component, [
				'ContentField',
				'TimestampField',
				'TimestampDisplayFormat',
				'ContactNameField',
				'ContactIdField',
				'ContactLink',
			]),
	},
};

function getVirtualCollectionItemOrDefault(selection: ReturnType<typeof resolveVirtualCollectionSelection>): Record<string, unknown> | null {
	if (!selection) {
		return null;
	}

	const item = getCollectionItems(selection.component, selection.parameter)[selection.itemIndex];
	if (item) {
		return item;
	}

	return virtualTemplateDefaults[selection.component.Type]?.[selection.parameter.name]?.(selection.component) ?? null;
}

export function AppItemProperties({
	bundle,
	selectedItem,
	selectedComponentPath,
	selectedSharedComponentContext,
	reactComponents = [],
	variableScope,
	onBundleChanged,
	onComponentChanged,
	onMoveComponentUp,
	onMoveComponentDown,
	onRemoveComponent,
	onSelectedComponentPathChanged,
}: AppItemPropertiesProps) {
	const { componentByName } = useUserInterfaceMetadata();
	const toast = useToastService();
	const copyToClipboard = useCopyToClipboard();
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

	const handleDeleteClick = useCallback(() => {
		setShowDeleteConfirm(true);
	}, []);

	const handleDeleteConfirm = useCallback(() => {
		setShowDeleteConfirm(false);
		if (onRemoveComponent) {
			onRemoveComponent();
		}
	}, [onRemoveComponent]);

	const handleDeleteCancel = useCallback(() => {
		setShowDeleteConfirm(false);
	}, []);

	// Handle copy component to clipboard
	const handleCopyComponent = useCallback(
		async (component: IComponentModel) => {
			await copyToClipboard(component, (c) => serializeWithTypeFirst(c, 2));
		},
		[copyToClipboard],
	);

	// Reads and validates a component from the clipboard, regenerating IDs.
	// Returns null and shows a toast on failure (invalid JSON, not a component, or read error).
	const readComponentFromClipboard = useCallback(async (): Promise<IComponentModel | null> => {
		try {
			const text = await navigator.clipboard.readText();
			const parsed = JSON.parse(text) as IComponentModel;
			if (!parsed || typeof parsed !== 'object' || !('Type' in parsed)) {
				toast.error('Clipboard does not contain a valid component');
				return null;
			}
			return regenerateComponentIds(parsed);
		} catch (error) {
			if (error instanceof SyntaxError) {
				toast.error('Clipboard does not contain valid JSON');
			} else {
				toast.error('Failed to read component from clipboard');
			}
			return null;
		}
	}, [toast]);

	// Handle paste component into view root
	const handlePasteIntoView = useCallback(
		async (view: ViewModel) => {
			const newComponent = await readComponentFromClipboard();
			if (!newComponent) return;

			const updatedView: ViewModel = { ...view, Children: [...view.Children, newComponent] };
			if (bundle && selectedItem) {
				onBundleChanged({ ...bundle, Views: bundle.Views.map((v) => (v.Id === selectedItem.id ? updatedView : v)) });
				toast.success('Component pasted');
			}
		},
		[bundle, selectedItem, onBundleChanged, toast, readComponentFromClipboard],
	);

	// Handle paste component into shared component root
	const handlePasteIntoSharedComponent = useCallback(
		async (sharedComponent: SharedComponentModel) => {
			const newComponent = await readComponentFromClipboard();
			if (!newComponent) return;

			const container = sharedComponent.Component as IContainerComponentModel;
			const updatedSharedComponent: SharedComponentModel = {
				...sharedComponent,
				Component: { ...container, Children: [...(container.Children ?? []), newComponent] },
			};
			if (bundle && selectedItem) {
				onBundleChanged({ ...bundle, SharedComponents: bundle.SharedComponents.map((c) => (c.Id === selectedItem.id ? updatedSharedComponent : c)) });
				toast.success('Component pasted');
			}
		},
		[bundle, selectedItem, onBundleChanged, toast, readComponentFromClipboard],
	);
	const handlePasteComponent = useCallback(async () => {
		const newComponent = await readComponentFromClipboard();
		if (!newComponent || !bundle || !selectedItem || !selectedComponentPath) return;

		let container: IContainerComponentModel | null = null;
		let pathToUse = selectedComponentPath;

		if (selectedItem.type === ITEM_TYPE_VIEW) {
			const view = bundle.Views.find((v) => v.Id === selectedItem.id);
			if (view) {
				container = view as IContainerComponentModel;
			}
		} else if (selectedItem.type === ITEM_TYPE_SHARED_COMPONENT) {
			const sharedComponent = bundle.SharedComponents.find((c) => c.Id === selectedItem.id);
			if (sharedComponent) {
				container = { ...sharedComponent.Component } as IContainerComponentModel;
				if (selectedComponentPath[0] === container.Id) {
					pathToUse = selectedComponentPath.slice(1);
				}
			}
		}

		if (!container) return;

		const updatedContainer = addComponentAtPath(container as any, pathToUse, newComponent);

		if (selectedItem.type === ITEM_TYPE_VIEW) {
			onBundleChanged({ ...bundle, Views: bundle.Views.map((v) => (v.Id === selectedItem.id ? (updatedContainer as any) : v)) });
		} else if (selectedItem.type === ITEM_TYPE_SHARED_COMPONENT) {
			const sharedComponent = bundle.SharedComponents.find((c) => c.Id === selectedItem.id);
			if (sharedComponent) {
				onBundleChanged({
					...bundle,
					SharedComponents: bundle.SharedComponents.map((c) => (c.Id === selectedItem.id ? { ...sharedComponent, Component: updatedContainer } : c)),
				});
			}
		}

		toast.success('Component pasted');
	}, [bundle, selectedItem, selectedComponentPath, onBundleChanged, toast, readComponentFromClipboard]);

	// Handle paste from clipboard replacing the currently selected component
	const handleReplaceComponent = useCallback(async () => {
		if (!bundle || !selectedItem || !selectedComponentPath) return;

		const newComponent = await readComponentFromClipboard();
		if (!newComponent) return;

		if (selectedItem.type === ITEM_TYPE_VIEW) {
			const view = bundle.Views.find((v) => v.Id === selectedItem.id);
			if (!view) return;
			const updatedView = updateComponentAtPath(view as unknown as IContainerComponentModel, selectedComponentPath, newComponent);
			onBundleChanged({ ...bundle, Views: bundle.Views.map((v) => (v.Id === selectedItem.id ? (updatedView as unknown as ViewModel) : v)) });
			// Update selection path: parent path stays the same, last segment becomes new component's ID
			const newPath = [...selectedComponentPath.slice(0, -1), newComponent.Id];
			onSelectedComponentPathChanged?.(newPath);
		} else if (selectedItem.type === ITEM_TYPE_SHARED_COMPONENT) {
			const sharedComponent = bundle.SharedComponents.find((c) => c.Id === selectedItem.id);
			if (!sharedComponent) return;
			const relativePath = selectedComponentPath[0] === sharedComponent.Component.Id ? selectedComponentPath.slice(1) : selectedComponentPath;
			// When relativePath is empty, the root component itself is selected — replace it directly
			const updatedComponent =
				relativePath.length === 0
					? (newComponent as IContainerComponentModel)
					: updateComponentAtPath({ ...sharedComponent.Component } as IContainerComponentModel, relativePath, newComponent);
			onBundleChanged({
				...bundle,
				SharedComponents: bundle.SharedComponents.map((c) => (c.Id === selectedItem.id ? { ...sharedComponent, Component: updatedComponent } : c)),
			});
			// Update selection path to reflect new component ID
			if (relativePath.length === 0) {
				// Root was replaced: new path is just [newComponent.Id]
				onSelectedComponentPathChanged?.([newComponent.Id]);
			} else {
				// Non-root: parent path stays, last segment becomes new component's ID
				const parentPrefix = selectedComponentPath.slice(0, -1);
				onSelectedComponentPathChanged?.([...parentPrefix, newComponent.Id]);
			}
		}

		toast.success('Component replaced');
	}, [bundle, selectedItem, selectedComponentPath, onBundleChanged, onSelectedComponentPathChanged, toast, readComponentFromClipboard]);

	// Determine move button states (memoized to avoid recomputing on every render)
	const moveButtonStates = useMemo((): { canMoveUp: boolean; canMoveDown: boolean; showMoveButtons: boolean } => {
		if (!bundle || !selectedComponentPath || selectedComponentPath.length === 0) {
			return { canMoveUp: false, canMoveDown: false, showMoveButtons: false };
		}

		if (getPotentialCollectionItemSelection(selectedComponentPath)) {
			return { canMoveUp: false, canMoveDown: false, showMoveButtons: false };
		}

		let container: IContainerComponentModel | null = null;
		let pathToUse = selectedComponentPath;

		if (selectedItem?.type === ITEM_TYPE_VIEW) {
			const view = bundle.Views.find((v) => v.Id === selectedItem.id);
			if (view) {
				container = view as IContainerComponentModel;
			}
		} else if (selectedItem?.type === ITEM_TYPE_SHARED_COMPONENT) {
			const sharedComponent = bundle.SharedComponents.find((c) => c.Id === selectedItem.id);
			if (sharedComponent) {
				container = { ...sharedComponent.Component } as IContainerComponentModel;
				// Strip root component ID from path for shared components
				if (selectedComponentPath[0] === container.Id) {
					pathToUse = selectedComponentPath.slice(1);
				}
			}
		}

		if (!container) {
			return { canMoveUp: false, canMoveDown: false, showMoveButtons: false };
		}

		const parentInfo = getParentInfo(container, pathToUse);
		if (!parentInfo) {
			return { canMoveUp: false, canMoveDown: false, showMoveButtons: false };
		}

		return {
			canMoveUp: parentInfo.currentIndex > 0,
			canMoveDown: parentInfo.currentIndex < parentInfo.parentChildren.length - 1,
			showMoveButtons: true,
		};
	}, [bundle, selectedComponentPath, selectedItem]);

	// Helper function to render root-level item buttons (for View or SharedComponent root)
	const renderRootItemButtons = (canPaste: boolean, onPaste: () => void, onCopy?: () => void) => {
		return (
			<Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', borderBottom: 1, borderColor: 'divider', pb: 1 }}>
				<Box sx={{ flex: 1 }} />
				{onCopy && (
					<Tooltip title="Copy component">
						<IconButton size="small" onClick={onCopy} color="primary">
							<ContentCopyIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				)}
				<Tooltip title="Paste component into root">
					<span>
						<IconButton size="small" onClick={onPaste} color="primary" disabled={!canPaste}>
							<ContentPasteIcon fontSize="small" />
						</IconButton>
					</span>
				</Tooltip>
				<Box sx={{ flex: 1 }} />
			</Box>
		);
	};

	// Toolbar shown above any selected component (known or unknown)
	const renderComponentToolbar = (component: IComponentModel, isSharedComponentRoot: boolean, isContainer: boolean) => (
		<Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', borderBottom: 1, borderColor: 'divider', pb: 1 }}>
			<Tooltip title="Move component up">
				<span>
					<IconButton size="small" onClick={onMoveComponentUp} disabled={!moveButtonStates.canMoveUp || isSharedComponentRoot} color="warning">
						<ArrowUpwardIcon fontSize="small" />
					</IconButton>
				</span>
			</Tooltip>
			<Tooltip title="Move component down">
				<span>
					<IconButton size="small" onClick={onMoveComponentDown} disabled={!moveButtonStates.canMoveDown || isSharedComponentRoot} color="warning">
						<ArrowDownwardIcon fontSize="small" />
					</IconButton>
				</span>
			</Tooltip>
			<Box sx={{ flex: 1 }} />
			<Tooltip title="Copy component">
				<IconButton size="small" onClick={() => handleCopyComponent(component)} color="primary">
					<ContentCopyIcon fontSize="small" />
				</IconButton>
			</Tooltip>
			{isContainer && (
				<Tooltip title="Paste component">
					<span>
						<IconButton size="small" onClick={handlePasteComponent} color="warning">
							<ContentPasteIcon fontSize="small" />
						</IconButton>
					</span>
				</Tooltip>
			)}
			<Tooltip title="Paste and replace selected component">
				<span>
					<IconButton size="small" onClick={handleReplaceComponent} color="warning">
						<ContentPasteGoIcon fontSize="small" />
					</IconButton>
				</span>
			</Tooltip>
			<Box sx={{ flex: 1 }} />
			<Tooltip title="Delete component">
				<span>
					<IconButton size="small" onClick={handleDeleteClick} color="error">
						<DeleteIcon fontSize="small" />
					</IconButton>
				</span>
			</Tooltip>
		</Box>
	);

	const applySharedComponentUpdate = (sharedComponent: SharedComponentModel, componentPath: ComponentPath, updatedComponent: IComponentModel) => {
		if (!bundle) {
			return;
		}

		const relativePath = componentPath[0] === sharedComponent.Component.Id ? componentPath.slice(1) : componentPath;
		const updatedRoot =
			componentPath.length === 1 && componentPath[0] === sharedComponent.Component.Id
				? (updatedComponent as IContainerComponentModel)
				: updateComponentAtPath(sharedComponent.Component as IContainerComponentModel, relativePath, updatedComponent);

		onBundleChanged({
			...bundle,
			SharedComponents: bundle.SharedComponents.map((c) => (c.Id === sharedComponent.Id ? { ...sharedComponent, Component: updatedRoot } : c)),
		});
	};

	// Helper function to render component properties (used by both View and SharedComponent)
	const renderComponentProperties = (component: IComponentModel | null, isSharedComponentRoot: boolean = false) => {
		if (!component) return null;
		const metadata = componentByName.get(component.Type);

		const isContainer = !!metadata?.isContainer;

		if (!metadata) {
			return (
				<Stack spacing={2}>
					{renderComponentToolbar(component, isSharedComponentRoot, false)}
					<Typography variant="body2" color="text.secondary">
						Unknown component type: {component.Type}
					</Typography>
				</Stack>
			);
		}

		const properties = (
			<ComponentProperties
				component={component}
				metadata={metadata}
				reactComponents={reactComponents}
				variableScope={variableScope}
				onComponentChanged={(updated) => {
					if (onComponentChanged) {
						onComponentChanged(updated);
					}
				}}
			/>
		);

		return (
			<Stack spacing={2}>
				{renderComponentToolbar(component, isSharedComponentRoot, isContainer)}
				{properties}
			</Stack>
		);
	};

	// If a component is selected within a view or shared component, show component properties
	if (selectedComponentPath && bundle && (selectedItem?.type === ITEM_TYPE_VIEW || selectedItem?.type === ITEM_TYPE_SHARED_COMPONENT)) {
		if (selectedItem.type === ITEM_TYPE_VIEW && selectedSharedComponentContext) {
			const resolution = resolveSharedComponentSelection(bundle, selectedComponentPath, selectedSharedComponentContext);
			if (!resolution) return null;

			const selection = resolveVirtualCollectionSelection(resolution.pathInSharedComponent, componentByName, (path) =>
				getComponentFromRoot(resolution.root, path),
			);
			if (selection) {
				const item = getVirtualCollectionItemOrDefault(selection);
				if (!item) return null;

				return (
					<VirtualCollectionItemProperties
						selection={selection}
						item={item}
						variableScope={variableScope}
						onOwnerComponentChanged={(updatedComponent) => applySharedComponentUpdate(resolution.sharedComponent, selection.componentPath, updatedComponent)}
						onSelectedComponentPathChanged={onSelectedComponentPathChanged}
						mapSelectionPath={(path) => [...selectedSharedComponentContext.referenceComponentPath, ...path]}
					/>
				);
			}
		}

		if (selectedItem.type === ITEM_TYPE_VIEW) {
			const view = bundle.Views.find((v) => v.Id === selectedItem.id);
			if (!view) return null;

			const selection = resolveVirtualCollectionSelection(selectedComponentPath, componentByName, (path) => getComponentAtPath(view.Children, path));
			if (selection) {
				const item = getVirtualCollectionItemOrDefault(selection);
				if (!item) return null;

				return (
					<VirtualCollectionItemProperties
						selection={selection}
						item={item}
						variableScope={variableScope}
						onOwnerComponentChanged={(updatedComponent) => {
							const updatedView = updateComponentAtPath(view as unknown as IContainerComponentModel, selection.componentPath, updatedComponent);
							onBundleChanged({ ...bundle, Views: bundle.Views.map((v) => (v.Id === view.Id ? (updatedView as unknown as ViewModel) : v)) });
						}}
						onSelectedComponentPathChanged={onSelectedComponentPathChanged}
					/>
				);
			}
		}

		if (selectedItem.type === ITEM_TYPE_SHARED_COMPONENT) {
			const sharedComponent = bundle.SharedComponents.find((c) => c.Id === selectedItem.id);
			if (!sharedComponent) return null;

			const selection = resolveVirtualCollectionSelection(selectedComponentPath, componentByName, (path) =>
				getComponentFromRoot(sharedComponent.Component as IContainerComponentModel, path),
			);
			if (selection) {
				const item = getVirtualCollectionItemOrDefault(selection);
				if (!item) return null;

				return (
					<VirtualCollectionItemProperties
						selection={selection}
						item={item}
						variableScope={variableScope}
						onOwnerComponentChanged={(updatedComponent) => applySharedComponentUpdate(sharedComponent, selection.componentPath, updatedComponent)}
						onSelectedComponentPathChanged={onSelectedComponentPathChanged}
					/>
				);
			}
		}

		// Special handling for dialog actions container: ['actions']
		if (selectedComponentPath[0] === 'actions' && selectedComponentPath.length === 1 && selectedItem.type === ITEM_TYPE_VIEW) {
			const view = bundle.Views.find((v) => v.Id === selectedItem.id);
			if (!view || !view.Dialog) return null;

			return (
				<Stack spacing={2}>
					<Typography variant="subtitle2" color="text.secondary">
						Dialog Actions Container
					</Typography>
					<Typography variant="body2" color="text.secondary">
						{view.Dialog.Actions?.length || 0} action(s)
					</Typography>
					<Typography variant="caption" color="text.secondary">
						Select an individual action button to edit its properties, or use the Dialog properties panel to add/remove actions.
					</Typography>
				</Stack>
			);
		}

		// Special handling for dialog actions path: ['actions', index]
		if (selectedComponentPath[0] === 'actions' && selectedComponentPath.length === 2 && selectedItem.type === ITEM_TYPE_VIEW) {
			const view = bundle.Views.find((v) => v.Id === selectedItem.id);
			if (!view || !view.Dialog?.Actions) return null;

			const actionIndex = parseInt(selectedComponentPath[1], 10);
			const action = view.Dialog.Actions[actionIndex];
			if (!action) return null;

			// Look up virtual component metadata for DialogAction
			const metadata = componentByName.get('DialogAction');
			if (!metadata) return null;

			// Convert DialogActionModel to IComponentModel-like structure
			// Note: Don't include Type in the spread as it's already set above
			const { Type: _actionType, ...actionProperties } = action;
			const actionAsComponent: IComponentModel = {
				Id: `${view.Id}-action-${actionIndex}`,
				Type: 'DialogAction',
				Layout: {},
				...actionProperties, // Spread action properties (Label, IconName, IsDefault, Path, OnSelected, ConfirmationDialog)
			};

			return (
				<ComponentProperties
					component={actionAsComponent}
					metadata={metadata}
					variableScope={variableScope}
					onComponentChanged={(updated) => {
						// Extract the action properties from the updated component
						const updatedAction: DialogActionModel = {
							Type: updated.Type || action.Type,
							Label: (updated as any).Label || action.Label,
							IconName: (updated as any).IconName,
							IsDefault: (updated as any).IsDefault,
							Path: (updated as any).Path,
							OnSelected: (updated as any).OnSelected,
							ConfirmationDialog: (updated as any).ConfirmationDialog,
							Disabled: (updated as any).Disabled,
							DisabledTooltip: (updated as any).DisabledTooltip,
						};
						const updatedActions = [...view.Dialog!.Actions!];
						updatedActions[actionIndex] = updatedAction;
						const updatedView: ViewModel = {
							...view,
							Dialog: {
								...view.Dialog!,
								Actions: updatedActions,
							},
						};
						onBundleChanged({ ...bundle, Views: bundle.Views.map((v) => (v.Id === selectedItem.id ? updatedView : v)) });
					}}
				/>
			);
		}

		// Resolve the selected component from the bundle
		// This handles both normal View components and components inside SharedComponents (via ReferenceComponent)
		const { component, isSharedComponentRoot } = resolveSelectedComponent(bundle, selectedItem, selectedComponentPath, selectedSharedComponentContext);

		return (
			<>
				{renderComponentProperties(component, isSharedComponentRoot)}
				<ConfirmDialog
					open={showDeleteConfirm}
					title="Delete Component"
					message="Are you sure you want to delete this component? This action cannot be undone."
					confirmText="Delete"
					confirmColor="error"
					onConfirm={handleDeleteConfirm}
					onCancel={handleDeleteCancel}
				/>
			</>
		);
	}

	if (!bundle || !selectedItem) {
		return (
			<Typography variant="body2" color="textSecondary">
				Select an item to view its properties
			</Typography>
		);
	}

	if (selectedItem.type === ITEM_TYPE_SHARED_COMPONENT) {
		const component = bundle.SharedComponents.find((c) => c.Id === selectedItem.id);
		if (!component) return null;

		// Get the root component for paste capability check
		const rootComponent = component.Component as IComponentModel;
		const canPaste = rootComponent && componentByName.get(rootComponent.Type)?.isContainer;

		return (
			<Stack spacing={2}>
				{renderRootItemButtons(
					canPaste || false,
					() => handlePasteIntoSharedComponent(component),
					() => handleCopyComponent(rootComponent),
				)}
				<SharedComponentProperties
					component={component}
					onComponentChanged={(updated: SharedComponentModel) =>
						onBundleChanged({ ...bundle, SharedComponents: bundle.SharedComponents.map((c) => (c.Id === updated.Id ? updated : c)) })
					}
					variableScope={variableScope}
				/>
			</Stack>
		);
	}

	if (selectedItem.type === ITEM_TYPE_VIEW) {
		const view = bundle.Views.find((v) => v.Id === selectedItem.id);
		if (!view) return null;

		// Views are always containers (can accept pasted components)
		return (
			<Stack spacing={2}>
				{renderRootItemButtons(true, () => handlePasteIntoView(view))}
				<ViewProperties
					view={view}
					onViewChanged={(updated: ViewModel) => onBundleChanged({ ...bundle, Views: bundle.Views.map((v) => (v.Id === updated.Id ? updated : v)) })}
					variableScope={variableScope}
				/>
			</Stack>
		);
	}

	if (selectedItem.type === ITEM_TYPE_EMBEDDED_RESOURCE) {
		const resource = bundle.EmbeddedResources.find((r) => r.Id === selectedItem.id);
		if (!resource) return null;
		return (
			<EmbeddedResourceProperties
				resource={resource}
				onResourceChanged={(updated: EmbeddedResourceModel) =>
					onBundleChanged({ ...bundle, EmbeddedResources: bundle.EmbeddedResources.map((r) => (r.Id === updated.Id ? updated : r)) })
				}
			/>
		);
	}

	if (selectedItem.type === ITEM_TYPE_SETTINGS) {
		return <AppSettingsProperties bundle={bundle} onBundleChanged={onBundleChanged} />;
	}

	if (selectedItem.type === ITEM_TYPE_THEME) {
		return null;
	}

	return null;
}
